// web/src/toc.ts
// 页面内大纲侧边栏（TOC）：DOM 驱动，自动扫描正文标题生成目录。
//
// 设计要点：
// - 内容作者只需在正文容器（[data-toc-root]）内按层级写 h2 / h3；
//   缺少 id 的标题会自动补 slug 并回写，无需维护任何目录数组。
// - Quick Start 与 进阶教程 两类页面共用本模块。

export interface TocOptions {
  /** 正文容器选择器，默认为 [data-toc-root] */
  rootSelector?: string;
  /** 侧边栏挂载点选择器，默认为 [data-toc-mount] */
  mountSelector?: string;
  /** 侧边栏外层容器选择器（用于移动端开合与空态），默认为 [data-toc-panel] */
  panelSelector?: string;
  /** 双栏布局外层选择器（无标题时降级为单栏），默认为 [data-toc-layout] */
  layoutSelector?: string;
  /** 少于该数量的小节则不渲染侧边栏，默认 2 */
  minHeadings?: number;
}

const DEFAULTS: Required<TocOptions> = {
  rootSelector: "[data-toc-root]",
  mountSelector: "[data-toc-mount]",
  panelSelector: "[data-toc-panel]",
  layoutSelector: "[data-toc-layout]",
  minHeadings: 2,
};

/**
 * 吸顶头部高度 + 呼吸空间，滚动定位时扣除，避免标题被吸顶导航条遮住。
 * 站点页面的头部是 `.site-navbar`（唯一吸顶元素）；管理后台仍用旧的 `.topbar`。
 */
function stickyOffset(): number {
  const header =
    document.querySelector<HTMLElement>(".site-navbar") ??
    document.querySelector<HTMLElement>(".topbar");
  const h = header ? header.getBoundingClientRect().height : 0;
  return Math.round(h + 16);
}

/** 由标题文本生成锚点 id */
function slugify(text: string): string {
  const base = text
    .trim()
    .toLowerCase()
    .replace(/[\s\u3000]+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
  return base || "section";
}

/** 标题的可见文字（忽略锚点链接） */
export function headingLabel(el: HTMLElement): string {
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll(".h-anchor, [data-toc-skip-text]").forEach((n) => n.remove());
  return (clone.textContent || "").trim();
}

/**
 * 给容器内的 h2/h3 补齐 id 并追加可复制的「#」锚点链接。
 * 页面外壳（shell.ts）在构建左栏时调用；buildToc 会复用这些 id。
 */
export function decorateHeadings(root: ParentNode): void {
  const used = new Set<string>();
  const anchorLabel =
    document.documentElement.lang === "en"
      ? "Copy link to this section"
      : "复制本小节链接";

  root.querySelectorAll<HTMLElement>("h2, h3").forEach((el) => {
    if (!el.id) {
      const base = slugify(headingLabel(el) || "section");
      let id = base;
      let n = 2;
      while (used.has(id) || document.getElementById(id)) id = `${base}-${n++}`;
      el.id = id;
    }
    used.add(el.id);

    if (el.querySelector(":scope > .h-anchor")) return;
    const a = document.createElement("a");
    a.className = "h-anchor";
    a.href = `#${el.id}`;
    a.textContent = "#";
    a.setAttribute("aria-label", anchorLabel);
    a.setAttribute("data-toc-skip-text", "");
    a.addEventListener("click", () => {
      if (history.replaceState) history.replaceState(null, "", `#${el.id}`);
    });
    el.appendChild(a);
  });
}

interface HeadingEntry {
  el: HTMLElement;
  id: string;
  level: number;
  link: HTMLAnchorElement;
  li: HTMLLIElement;
}

let scrollHandler: (() => void) | null = null;
let progressHandler: (() => void) | null = null;
let activeId = "";

function clearPrevious(): void {
  if (scrollHandler) {
    window.removeEventListener("scroll", scrollHandler);
    window.removeEventListener("resize", scrollHandler);
    scrollHandler = null;
  }
  if (progressHandler) {
    window.removeEventListener("scroll", progressHandler);
    window.removeEventListener("resize", progressHandler);
    progressHandler = null;
  }
}

/** 保证每个标题都有唯一 id */
function ensureId(el: HTMLElement, used: Set<string>): string {
  let id = el.id;
  if (!id) {
    id = slugify(headingLabel(el) || "section");
    let candidate = id;
    let n = 2;
    while (used.has(candidate) || document.getElementById(candidate)) {
      candidate = `${id}-${n++}`;
    }
    id = candidate;
    el.id = id;
  }
  used.add(id);
  return id;
}

function buildList(headings: HeadingEntry[], mount: HTMLElement): void {
  mount.textContent = "";
  const topList = document.createElement("ul");
  topList.className = "toc-list toc-list-top";

  let currentSub: HTMLUListElement | null = null;
  let currentSubLi: HTMLLIElement | null = null;

  for (const h of headings) {
    const li = document.createElement("li");
    li.className = `toc-item toc-level-${h.level}`;

    const a = document.createElement("a");
    a.className = "toc-link";
    a.href = `#${h.id}`;
    a.textContent = headingLabel(h.el) || h.id;
    a.dataset.target = h.id;

    li.appendChild(a);
    h.link = a;
    h.li = li;

    if (h.level <= 2 || !currentSubLi) {
      topList.appendChild(li);
      currentSub = null;
    } else {
      // h3 归入上一个 h2 的子列表
      if (!currentSub) {
        currentSub = document.createElement("ul");
        currentSub.className = "toc-list toc-list-sub";
        currentSubLi.appendChild(currentSub);
      }
      currentSub.appendChild(li);
    }

    if (h.level <= 2) currentSubLi = li;
  }

  mount.appendChild(topList);
}

function scrollToId(id: string, smooth = true): void {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - stickyOffset();
  window.scrollTo({ top: Math.max(top, 0), behavior: smooth ? "smooth" : "auto" });
  if (history.replaceState) {
    history.replaceState(null, "", `#${id}`);
  }
}

function setActive(headings: HeadingEntry[], id: string): void {
  if (id === activeId) return;
  activeId = id;
  for (const h of headings) {
    const on = h.id === id;
    h.li.classList.toggle("is-active", on);
    if (on) {
      // 保证高亮项在侧边栏可视区域内
      const panel = h.li.closest<HTMLElement>(".toc-scroll");
      if (panel) {
        const r = h.li.getBoundingClientRect();
        const pr = panel.getBoundingClientRect();
        if (r.top < pr.top || r.bottom > pr.bottom) {
          panel.scrollTop += r.top - pr.top - pr.height / 2 + r.height / 2;
        }
      }
    }
  }
}

/** 滚动时计算「当前所处小节」：最后一个已越过阈值线的标题 */
function watchScroll(headings: HeadingEntry[]): void {
  let ticking = false;
  const update = () => {
    ticking = false;
    const line = stickyOffset() + 8;
    let current = headings[0]?.id || "";
    for (const h of headings) {
      const top = h.el.getBoundingClientRect().top;
      if (top - line <= 0) current = h.id;
      else break;
    }
    // 已滚到底部时强制高亮最后一节，避免末节过短无法触发
    const atBottom =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - 4;
    if (atBottom && headings.length) current = headings[headings.length - 1].id;
    setActive(headings, current);
  };
  scrollHandler = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };
  window.addEventListener("scroll", scrollHandler, { passive: true });
  window.addEventListener("resize", scrollHandler, { passive: true });
  update();
}

function watchProgress(panel: HTMLElement): void {
  const bar = panel.querySelector<HTMLElement>("[data-toc-progress]");
  if (!bar) return;
  const update = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
    bar.style.width = `${(p * 100).toFixed(2)}%`;
    panel.classList.toggle("has-scrolled", window.scrollY > 320);
  };
  progressHandler = update;
  window.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update, { passive: true });
  update();
}

function bindPanelBehaviors(
  panel: HTMLElement,
  opts: Required<TocOptions>,
): void {
  // 语言切换会触发重建；这里的监听不依赖 headings，绑定一次即可，避免重复叠加。
  if (panel.dataset.tocBound === "1") return;
  panel.dataset.tocBound = "1";

  const toggle = panel.querySelector<HTMLButtonElement>("[data-toc-toggle]");
  if (toggle) {
    toggle.addEventListener("click", () => {
      const open = panel.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }

  const top = panel.querySelector<HTMLButtonElement>("[data-toc-top]");
  if (top) {
    top.addEventListener("click", () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (history.replaceState) history.replaceState(null, "", location.pathname);
    });
  }

  const mount = panel.querySelector<HTMLElement>(opts.mountSelector);
  mount?.addEventListener("click", (ev) => {
    const a = (ev.target as HTMLElement).closest<HTMLAnchorElement>("a.toc-link");
    if (!a) return;
    ev.preventDefault();
    const id = a.dataset.target || a.getAttribute("href")?.slice(1) || "";
    if (!id) return;
    scrollToId(id);
    panel.classList.remove("is-open");
    document.body.classList.remove("rail-open");
    if (toggle) toggle.setAttribute("aria-expanded", "false");
  });
}

/**
 * 扫描正文标题并构建侧边栏。可在正文渲染完成后重复调用（会先清理上一次的监听）。
 */
export function buildToc(options: TocOptions = {}): void {
  const opts = { ...DEFAULTS, ...options };
  clearPrevious();

  const root = document.querySelector<HTMLElement>(opts.rootSelector);
  const panel = document.querySelector<HTMLElement>(opts.panelSelector);
  const layout = document.querySelector<HTMLElement>(opts.layoutSelector);
  const mount = document.querySelector<HTMLElement>(opts.mountSelector);
  if (!root || !panel || !mount) return;

  const nodes = Array.from(
    root.querySelectorAll<HTMLElement>("h2, h3"),
  ).filter((el) => !el.hasAttribute("data-toc-skip"));

  if (nodes.length < opts.minHeadings) {
    panel.setAttribute("hidden", "");
    layout?.classList.add("toc-empty");
    return;
  }
  panel.removeAttribute("hidden");
  layout?.classList.remove("toc-empty");

  const used = new Set<string>();
  const headings: HeadingEntry[] = nodes.map((el) => ({
    el,
    id: ensureId(el, used),
    level: el.tagName === "H2" ? 2 : 3,
    link: null as unknown as HTMLAnchorElement,
    li: null as unknown as HTMLLIElement,
  }));

  buildList(headings, mount);
  activeId = "";
  watchScroll(headings);
  watchProgress(panel);
  bindPanelBehaviors(panel, opts);

  // 深链接：带 hash 打开时定位一次（不滚动）
  if (location.hash.length > 1) {
    const id = decodeURIComponent(location.hash.slice(1));
    if (headings.some((h) => h.id === id)) {
      scrollToId(id, false);
    }
  }
}

/**
 * 绑定一次性的全局初始化：语言切换后重建（标题文本随语言变化）。
 * 重复调用安全。
 */
export function initTocOnLangChange(options: TocOptions = {}): void {
  document.addEventListener("langchange", () => buildToc(options));
}
